import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faFlask, faSync, faBolt, faSpinner, faShareSquare, faComment, faStethoscope, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { formatPatientAge } from '../../assets/Utils';
import ObjectMessagingModal from '../Messages/ObjectMessagingModal';
import { useAuth } from '../../hooks/useAuth';

const DoctorVisitsList = ({
    visits = [],
    doctorName,
    onOpenVisit,
    onRequestLab,
    onReferVisit,
    onGenerateSummary,
    showAllDoctors,
    onToggleAllDoctors,
    canToggleAllDoctors = true,
    selectedDate,
    onSelectedDateChange,
    onPreviousDate,
    onNextDate,
    onTodayDate,
    onRefresh,
    isRefreshing,
    canExecuteSummary,
    summaryLoadingByPatient,
    summaryErrorByPatient
}) => {
    const { hasPermission } = useAuth();
    const canMessage = hasPermission ? hasPermission('/messaging/send', 'Execute') : false;
    const [messagingPatient, setMessagingPatient] = React.useState(null);
    const [nameFilter, setNameFilter] = React.useState('');

    React.useEffect(() => {
        setNameFilter('');
    }, [selectedDate, showAllDoctors]);

    const todayValue = (() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    })();
    const activeDateValue = selectedDate || todayValue;
    const activeDate = new Date(`${activeDateValue}T00:00:00`);
    const headerDate = Number.isNaN(activeDate.getTime())
        ? activeDateValue
        : activeDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const isTodaySelected = activeDateValue === todayValue;

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const trimmedFilter = nameFilter.trim();
    const isFiltering = trimmedFilter.length > 0;
    const filteredVisits = React.useMemo(() => {
        if (!visits || visits.length === 0) return [];
        const query = trimmedFilter.toLowerCase();
        if (!query) return visits;
        return visits.filter(visit => {
            const patient = visit.Patient || {};
            const first = (patient.patient_first_name || '').toLowerCase();
            const last = (patient.patient_last_name || '').toLowerCase();
            const full = `${first} ${last}`.trim();
            const number = String(patient.patient_number || '').toLowerCase();
            return first.includes(query) || last.includes(query) || full.includes(query) || number.includes(query);
        });
    }, [visits, trimmedFilter]);

    const hasVisits = visits && visits.length > 0;
    const visitCountLabel = isFiltering
        ? `${filteredVisits.length} of ${visits.length} visit${visits.length !== 1 ? 's' : ''}`
        : `${visits.length} visit${visits.length !== 1 ? 's' : ''}`;

    return (
        <div className="doctor-visits-panel">
            <div className="visits-header">
                <div>
                    <h2>{showAllDoctors ? "All Doctors' Visits" : `${doctorName}'s Visits`}</h2>
                    <span className="header-date">{headerDate}</span>
                </div>
                <div className="visits-header-right">
                    <div className="visits-date-nav" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="visits-date-btn"
                            onClick={onPreviousDate}
                            type="button"
                            aria-label="View previous day"
                        >
                            Prev
                        </button>
                        <input
                            className="visits-date-input"
                            type="date"
                            value={activeDateValue}
                            onChange={(e) => onSelectedDateChange?.(e.target.value)}
                            aria-label="Select date"
                        />
                        <button
                            className="visits-date-btn"
                            onClick={onNextDate}
                            type="button"
                            aria-label="View next day"
                        >
                            Next
                        </button>
                        <button
                            className="visits-date-btn today"
                            onClick={onTodayDate}
                            type="button"
                            aria-label="Go to today"
                            disabled={isTodaySelected}
                        >
                            Today
                        </button>
                    </div>
                    {canToggleAllDoctors && (
                        <label className="visits-toggle">
                            <input type="checkbox" checked={showAllDoctors} onChange={onToggleAllDoctors} />
                            <span>All Doctors</span>
                        </label>
                    )}
                    {hasVisits && <span className="visit-count">{visitCountLabel}</span>}
                    <button
                        className={`visits-refresh-btn${isRefreshing ? ' spinning' : ''}`}
                        onClick={onRefresh}
                        disabled={isRefreshing}
                        title="Refresh dashboard"
                        aria-label="Refresh dashboard"
                    >
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                </div>
            </div>

            <div className="visits-find-patient">
                <FontAwesomeIcon icon={faSearch} className="find-icon" />
                <input
                    type="text"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    placeholder="Find patient by name or number"
                    aria-label="Find patient by name or number"
                    autoComplete="off"
                />
                {nameFilter ? (
                    <button
                        type="button"
                        className="find-clear"
                        onClick={() => setNameFilter('')}
                        aria-label="Clear patient search"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                ) : null}
            </div>

            {!hasVisits ? (
                <div className="visits-empty">
                    <div className="empty-icon">
                        <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                    <p>No visits scheduled for this date</p>
                </div>
            ) : filteredVisits.length === 0 ? (
                <div className="visits-empty">
                    <div className="empty-icon">
                        <FontAwesomeIcon icon={faSearch} />
                    </div>
                    <p>No visits match “{trimmedFilter}”</p>
                </div>
            ) : filteredVisits.map(visit => {
                const patient = visit.Patient || {};
                const patientId = patient.patient_id;
                const patientName = `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim();
                const age = formatPatientAge(patient.patient_dob);
                const sex = patient.patient_sex || '';
                const company = patient.company || '';
                const demographics = [age, sex, company].filter(Boolean).join(', ');
                const flags = visit.flags || {};
                const summaryInfo = visit.patientSummary || { status: 'missing', summary: '' };
                const summaryLoading = !!summaryLoadingByPatient?.[patientId];
                const summaryError = summaryErrorByPatient?.[patientId] || '';
                const chronicDiagnoses = Array.isArray(summaryInfo.chronic_diagnoses) ? summaryInfo.chronic_diagnoses : [];
                const allergies = Array.isArray(summaryInfo.allergies) ? summaryInfo.allergies : [];
                const isReferred = Boolean(visit.outgoing_referral_doctor);

                return (
                    <div
                        key={visit.visit_id}
                        className="visit-card"
                        onClick={() => onOpenVisit(visit)}
                    >
                        <div className="visit-info">
                            <p className="patient-name">{patientName || 'Unknown Patient'}</p>
                            <p className="patient-number">
                                <Link
                                    to="/patient-card"
                                    state={{ patient }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="patient-link"
                                >
                                    #{patient.patient_number}
                                </Link>
                                 &middot; {formatTime(visit.visit_date)}
                            </p>
                            {demographics && <p className="patient-demographics">{demographics}</p>}
                            {showAllDoctors && visit.assigned_doctor && (
                                <p className="visit-doctor-name">Dr. {visit.assigned_doctor}</p>
                            )}
                            <div className="visit-badges">
                                {flags.missingNotes && <span className="visit-badge missing-notes">Missing Notes</span>}
                                {flags.abnormalVitals && <span className="visit-badge abnormal-vitals">Abnormal Vitals</span>}
                                {flags.hasPrescription
                                    ? <span className="visit-badge has-prescription">Rx Given</span>
                                    : <span className="visit-badge no-prescription">No Rx</span>
                                }
                                {!patient.company && (
                                    visit.invoiced
                                        ? <span className="visit-badge paid">Paid</span>
                                        : <span className="visit-badge unpaid">Unpaid</span>
                                )}
                            </div>
                            {visit.diagnosis && (
                                <p className="diagnosis-snippet">{visit.diagnosis}</p>
                            )}
                            <div className="visit-card-actions">
                                <button
                                    className="enter-treatment-btn"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenVisit(visit);
                                    }}
                                    aria-label={`Enter Treatment for ${patientName || 'patient'}`}
                                >
                                    <FontAwesomeIcon icon={faStethoscope} /> Enter Treatment
                                </button>
                                <button
                                    className="lab-request-btn"
                                    type="button"
                                    disabled={isReferred}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isReferred) return;
                                        onRequestLab(visit);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFlask} /> Request Lab/Radiology
                                </button>
                                {visit.assigned_doctor === doctorName && (
                                    visit.outgoing_referral_doctor ? (
                                        <span className="referred-visit-label">
                                            Referred to doctor: {visit.outgoing_referral_doctor}
                                        </span>
                                    ) : (
                                        <button
                                            className="refer-visit-btn"
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onReferVisit(visit);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faShareSquare} /> Refer
                                        </button>
                                    )
                                )}
                                {canMessage && (
                                    <button
                                        className="message-visit-btn"
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMessagingPatient(patient);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faComment} /> Message
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="visit-ai-summary" onClick={(e) => e.stopPropagation()}>
                            <div className="visit-ai-summary-header">
                                <div className="visit-ai-summary-header-left">
                                    <span className="visit-ai-summary-title">AI Summary</span>

                                    {summaryInfo.status === 'stale' && (
                                        <span className="visit-ai-summary-status stale">New clinical data available</span>
                                    )}

                                    {summaryInfo.status === 'ready' && summaryInfo.summary && (
                                        <span className="visit-ai-summary-status ready">Up to date</span>
                                    )}
                                </div>

                                <div className="visit-ai-summary-header-right">
                                    {canExecuteSummary && (
                                        <button
                                            className={`visit-ai-summary-action ${summaryInfo.status === 'stale' ? 'stale' : ''}`}
                                            onClick={() => onGenerateSummary(visit)}
                                            disabled={summaryLoading}
                                            title={summaryInfo.status === 'missing' ? 'Generate summary' : 'Refresh summary'}
                                            aria-label={summaryInfo.status === 'missing' ? 'Generate summary' : 'Refresh summary'}
                                        >
                                            <FontAwesomeIcon icon={summaryLoading ? faSpinner : (summaryInfo.status === 'missing' ? faBolt : faSync)} className={summaryLoading ? 'spinning' : ''} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {summaryInfo.summary ? (
                                <p className="visit-ai-summary-text">{summaryInfo.summary}</p>
                            ) : (
                                <p className="visit-ai-summary-empty">No cached summary yet.</p>
                            )}

                            {(chronicDiagnoses.length > 0 || allergies.length > 0) && (
                                <div className="visit-chronic-section">
                                    <p className="visit-chronic-title">Chronic Conditions &amp; Allergies</p>
                                    {chronicDiagnoses.length > 0 && (
                                        <div className="visit-chronic-badges">
                                            {chronicDiagnoses.map((item, index) => {
                                                const diagnosis = typeof item === 'string' ? item : item?.diagnosis;
                                                if (!diagnosis) return null;
                                                return (
                                                    <span key={`chronic-${visit.visit_id}-${index}`} className="visit-chronic-badge">
                                                        {diagnosis}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {allergies.length > 0 && (
                                        <div className="visit-allergy-badges">
                                            {allergies.map((item, index) => {
                                                const substance = typeof item === 'string' ? item : item?.substance;
                                                if (!substance) return null;
                                                return (
                                                    <span key={`allergy-${visit.visit_id}-${index}`} className="visit-allergy-badge">
                                                        Allergy: {substance}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {summaryError && <span className="visit-ai-summary-error">{summaryError}</span>}
                        </div>
                    </div>
                );
            })}

            {messagingPatient && (
                <ObjectMessagingModal
                    patient={messagingPatient}
                    placement="patient-card"
                    showButton={false}
                    isOpen
                    onClose={() => setMessagingPatient(null)}
                />
            )}
        </div>
    );
};

export default DoctorVisitsList;
